/**
 * スクロールアニメーション
 * 1) 背景・矩形など：スクロール連動（scene progress -> --p）
 * 2) テキスト：画面に入ったら開始（IntersectionObserver）
 */

class ScrollAnimations {
  constructor() {
    this.scenes = [...document.querySelectorAll("[data-scene]")];
    this.clamp = (n, a = 0, b = 1) => Math.min(b, Math.max(a, n));
    this.enterObserver = null;

    this.init();
  }

  init() {
    this.updateScrollDriven();
    this.setupEnterAnimations();

    window.addEventListener("scroll", () => this.updateScrollDriven(), {
      passive: true,
    });
    window.addEventListener("resize", () => this.updateScrollDriven());

    this.observeDOMMutations();
  }

  // 1) 背景・矩形など：スクロール連動（scene progress -> --p）
  updateScrollDriven() {
    const vh = window.innerHeight;
    const y = window.scrollY;

    for (const scene of this.scenes) {
      const rect = scene.getBoundingClientRect();
      const top = y + rect.top;
      const height = scene.offsetHeight;
      const max = Math.max(1, height - vh);
      const progress = this.clamp((y - top) / max);
      scene.style.setProperty("--progress", progress);

      const targets = scene.querySelectorAll("[data-anim]");
      targets.forEach((el) => {
        // テキスト入場トリガーと混ざらないよう、range を持つものだけ進捗を適用
        const hasRange = el.hasAttribute("data-range");
        if (!hasRange) return;

        const range = (el.getAttribute("data-range") || "0 1")
          .trim()
          .split(/\s+/)
          .map(Number);
        const start = Number.isFinite(range[0]) ? range[0] : 0;
        const end = Number.isFinite(range[1]) ? range[1] : 1;
        const p = this.clamp(
          (progress - start) / Math.max(0.0001, end - start)
        );
        el.style.setProperty("--p", p);
      });
    }
  }

  // 2) テキスト：画面に入ったら開始（IntersectionObserver）
  setupEnterAnimations(root = document) {
    if (!this.enterObserver) {
      this.createEnterObserver();
    }

    const targets = this.collectEnterTargets(root);
    targets.forEach((el) => {
      const delay = parseInt(el.getAttribute("data-delay") || "0", 10);
      el.style.transitionDelay = `${Math.max(0, delay)}ms`;
      el.setAttribute("data-enter-bound", "true");
      this.enterObserver.observe(el);
    });
  }

  createEnterObserver() {
    this.enterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.enterObserver.unobserve(entry.target);
        });
      },
      {
        // 下端が少し入ったタイミングで開始
        threshold: 0.15,
        rootMargin: "0px 0px -10% 0px",
      }
    );
  }

  collectEnterTargets(root = document) {
    if (!root) return [];

    const selector = '[data-trigger="enter"]';
    const targets = [];
    const isElementNode = root.nodeType === 1;

    if (
      isElementNode &&
      typeof root.matches === "function" &&
      root.matches(selector) &&
      !root.hasAttribute("data-enter-bound")
    ) {
      targets.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      const descendants = root.querySelectorAll(selector);
      descendants.forEach((el) => {
        if (!el.hasAttribute("data-enter-bound")) {
          targets.push(el);
        }
      });
    }

    return targets;
  }

  observeDOMMutations() {
    if (typeof MutationObserver === "undefined" || !document.body) return;

    this.domObserver = new MutationObserver((mutations) => {
      let shouldRefreshScenes = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1 && node.nodeType !== 11) return;

          this.setupEnterAnimations(node);

          if (shouldRefreshScenes) return;

          const hasScene =
            (node.nodeType === 1 &&
              typeof node.matches === "function" &&
              node.matches("[data-scene]")) ||
            (typeof node.querySelector === "function" &&
              node.querySelector("[data-scene]"));

          if (hasScene) shouldRefreshScenes = true;
        });
      });

      if (shouldRefreshScenes) {
        this.refreshScenes();
      }
    });

    this.domObserver.observe(document.body, { childList: true, subtree: true });
  }

  refreshScenes() {
    this.scenes = [...document.querySelectorAll("[data-scene]")];
    this.updateScrollDriven();
  }
}

// 初期化
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new ScrollAnimations();
  });
} else {
  new ScrollAnimations();
}
